import { useCallback, useContext, useEffect, useState } from 'react'
import { Alert, Tabs, Tab, Box, Button, Menu, MenuItem } from '@mui/material'
import { ContentCopy, DataObject, FileDownload, Shuffle } from '@mui/icons-material'

import { SessionContext } from '../../context/SessionContext'
import { showToast } from '../commonUnit/Toast'
import { getReasoningGapActivity, getReasoningGapPartners, getReasoningGapSubmissions, pairReasoningGapSession } from '../../service/reasoningGapService'
import ReasoningGapExerciseEditor from './ReasoningGapExerciseEditor'
import ReasoningGapSubmissions, { download, toCsv } from './ReasoningGapSubmissions'
import RawDataModal from '../commonUnit/RawDataPanel'
import '../../css/taskcard.scss'
import './reasoningGap.scss'

// Instructor page for a GapCode / GapThinking session. Laid out like the GUI exercise editor
// (GuiExerciseEditor.jsx): tabs on the left, the student link / Distribute / data actions on
// the right, and a read-only exercise view with a single Edit Exercise mode.

const POLL_MS = 15000

export default function ReasoningGapInstructor () {
  const { session } = useContext(SessionContext)
  const [tabValue, setTabValue] = useState(0)
  const [data, setData] = useState(null)
  const [error, setError] = useState('')
  const [isEditing, setIsEditing] = useState(false)
  const [exportAnchor, setExportAnchor] = useState(null)
  const [pairAnchor, setPairAnchor] = useState(null)
  const [partners, setPartners] = useState(null)
  const [rawDataModalOpen, setRawDataModalOpen] = useState(false)

  // The other group's session this one is paired with (saved on both), or null. When paired,
  // the student link is the shared /rg link, which splits students between the two groups.
  // Last class, the single-session link got shared instead and nearly everyone landed in one
  // group, so Distribute hands out the shared link whenever a pairing exists.
  const [pairedWith, setPairedWith] = useState(null)
  const [sameTask, setSameTask] = useState(true)
  const sharedLinkFor = partnerId => {
    const [codeId, thinkingId] = session.type === 'GapCode' ? [session.id, partnerId] : [partnerId, session.id]
    return `${window.location.origin}/rg/${codeId}/${thinkingId}`
  }

  const loadPairing = useCallback(async () => {
    if (!session?.id) return
    try {
      const result = await getReasoningGapPartners(session.id)
      setPartners(result.partners)
      setPairedWith(result.pairedWith || null)
      setSameTask(result.sameTask !== false)
    } catch (err) {
      setPartners([])
    }
  }, [session?.id])
  useEffect(() => { loadPairing() }, [loadPairing])

  const openPairMenu = async (e) => {
    setPairAnchor(e.currentTarget)
    loadPairing()
  }
  const pairWith = async (partner) => {
    setPairAnchor(null)
    try {
      const result = await pairReasoningGapSession(session.id, partner.id)
      setPairedWith(partner.id)
      setSameTask(result.sameTask !== false)
      const link = sharedLinkFor(partner.id)
      try {
        await navigator.clipboard.writeText(link)
        showToast(`Paired with #${partner.id}. Shared link copied: ${link}`, 'success', 5000)
      } catch (err) {
        showToast(`Paired with #${partner.id}. Shared link: ${link}`, 'success', 8000)
      }
    } catch (err) {
      showToast(err.response?.data?.error || 'Could not pair the sessions', 'error')
    }
  }

  const load = useCallback(async () => {
    if (!session?.id) return
    try {
      setData(await getReasoningGapSubmissions(session.id))
      setError('')
    } catch (e) {
      setError(e.response?.data?.error || e.message)
    }
  }, [session?.id])

  useEffect(() => {
    load()
    const timer = setInterval(load, POLL_MS)
    return () => clearInterval(timer)
  }, [load])

  if (!session) return null
  const studentLink = pairedWith ? sharedLinkFor(pairedWith) : `${window.location.origin}/session/${session.id}`
  const submissionCount = data ? data.submissions.length : 0

  const handleDistribute = async () => {
    if (isEditing) {
      showToast('You have unsaved exercise changes - Save them first, or students won\'t see them.', 'error')
    }
    try {
      await navigator.clipboard.writeText(studentLink)
      if (!isEditing) {
        if (pairedWith) showToast(`Shared link copied. Each student is placed in this session or #${pairedWith}, keeping the groups even.`, 'success', 4000)
        else showToast('Link copied. This session is not paired, so everyone who uses this link joins this one group. Use "Random group link" to split students between two groups.', 'warning', 7000)
      }
    } catch (e) {
      showToast('Failed to copy link', 'error')
    }
  }

  const handleExport = async (format) => {
    setExportAnchor(null)
    const stamp = new Date().toISOString().slice(0, 10)
    if (format === 'activity') {
      try {
        const eventsByUser = await getReasoningGapActivity(session.id)
        const students = Object.fromEntries((data?.submissions || []).map(s => [s.user_id, s.email]))
        const rows = Object.entries(eventsByUser || {}).map(([userId, events]) => ({ userId: +userId, email: students[userId] || null, events }))
        download(`reasoning-gap-${session.id}-${stamp}-activity.json`, JSON.stringify({ sessionId: session.id, students: rows }, null, 2), 'application/json')
      } catch (e) {
        showToast('Could not load the activity log', 'error')
      }
    } else if (format === 'csv') download(`reasoning-gap-${session.id}-${stamp}.csv`, toCsv(data), 'text/csv')
    else download(`reasoning-gap-${session.id}-${stamp}.json`, JSON.stringify(data, null, 2), 'application/json')
  }

  return (
    <div className="gui-exercise-editor rg-editor-page">
      <Box
        sx={{
          borderBottom: 1,
          borderColor: 'divider',
          display: 'flex',
          flexWrap: 'wrap',
          justifyContent: 'space-between',
          alignItems: 'center',
          gap: 1,
          mb: 2,
        }}>
        <Tabs value={tabValue} onChange={(e, v) => setTabValue(v)} aria-label="reasoning gap tabs">
          <Tab label="Exercise Editor" />
          <Tab label={`Submissions (${submissionCount})`} />
        </Tabs>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1 }}>
          <span className="gui-session-link">{studentLink}</span>
          <Button variant="outlined" size="small" startIcon={<ContentCopy />} onClick={handleDistribute}>
            Distribute
          </Button>
          <Button variant="outlined" size="small" startIcon={<Shuffle />} onClick={openPairMenu}>
            {pairedWith ? `Paired with #${pairedWith}` : 'Random group link'}
          </Button>
          <Menu anchorEl={pairAnchor} open={!!pairAnchor} onClose={() => setPairAnchor(null)}>
            <MenuItem disabled sx={{ whiteSpace: 'normal', maxWidth: 380 }}>
              Pair this session with a {session.type === 'GapCode' ? 'Thinking' : 'Code'} Ground Truth session on the same quiz. Distribute then copies one shared link that splits students evenly between the two.
            </MenuItem>
            {partners === null && <MenuItem disabled>Loading…</MenuItem>}
            {partners && partners.length === 0 && <MenuItem disabled>No {session.type === 'GapCode' ? 'Thinking' : 'Code'} Ground Truth session on this quiz yet. Create one first.</MenuItem>}
            {(partners || []).map(p => (
              <MenuItem key={p.id} selected={p.id === pairedWith} onClick={() => pairWith(p)}>
                {p.id === pairedWith ? 'Paired with' : 'Pair with'} #{p.id}: {p.subject}
              </MenuItem>
            ))}
          </Menu>
          {/* Per-student activity and replay, the same View Student Data as the other
              session types (events adapted in reasoningGapReplay.js). */}
          <Button variant="outlined" size="small" startIcon={<DataObject />} onClick={() => setRawDataModalOpen(true)}>
            View Student Data
          </Button>
          <Button
            variant="outlined"
            size="small"
            startIcon={<FileDownload />}
            disabled={!submissionCount}
            onClick={e => setExportAnchor(e.currentTarget)}
          >
            Export Data
          </Button>
          <Menu anchorEl={exportAnchor} open={!!exportAnchor} onClose={() => setExportAnchor(null)}>
            <MenuItem onClick={() => handleExport('csv')}>CSV (one row per attempt)</MenuItem>
            <MenuItem onClick={() => handleExport('json')}>JSON (submissions, attempts, conversations)</MenuItem>
            <MenuItem onClick={() => handleExport('activity')}>Activity log JSON (runs, pastes, focus, edits)</MenuItem>
          </Menu>
        </Box>
      </Box>

      {pairedWith && !sameTask && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          This session and its paired session #{pairedWith} show students a different task text or
          different examples. Both groups should see exactly the same quiz (only the feedback
          differs), so make the two match before sharing the link.
        </Alert>
      )}
      {tabValue === 0 && (
        <ReasoningGapExerciseEditor session={session} submissionCount={submissionCount} onEditingChange={setIsEditing} onContentChanged={loadPairing} />
      )}
      {tabValue === 1 && (
        <ReasoningGapSubmissions data={data} error={error} onChanged={load} />
      )}
      <RawDataModal open={rawDataModalOpen} onClose={() => setRawDataModalOpen(false)} />
    </div>
  )
}
